<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon color="primary">
          mdi-card-bulleted-outline
        </v-icon>
        <span class="px-2">{{ $t('Route Pass Plans') }}</span>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="showPlanDialog">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('Add Route Pass Plan') }}
        </v-btn>
      </v-card-title>
      <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :headers="translatedHeaders"
        :items="plans"
        :search="search"
        :footer-props="footerProps"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            :label="$t('search')"
            class="mx-4"
          ></v-text-field>
        </template>
        <template v-slot:item.name="{ item }">
          {{ $i18n.locale === 'ar' && item.name_ar ? item.name_ar : item.name_en }}
        </template>
        <template v-slot:item.period_type="{ item }">
          {{ $t(item.period_type) }}
        </template>
        <template v-slot:item.discount_percent="{ item }">
          {{ item.discount_percent }}%
        </template>
        <template v-slot:item.is_active="{ item }">
          <v-chip :color="item.is_active ? 'success' : 'grey'" dark>
            {{ item.is_active ? $t('Active') : $t('Inactive') }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon
            small
            class="mx-2"
            @click="editPlan(item)"
          >
            mdi-pencil
          </v-icon>
          <v-icon
            small
            class="mx-2"
            @click="toggleStatus(item)"
          >
            {{ item.is_active ? 'mdi-eye-off' : 'mdi-eye' }}
          </v-icon>
          <v-icon
            small
            class="mx-2"
            @click="deletePlan(item)"
          >
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-row justify="center">
      <v-dialog
        v-model="planDialog"
        persistent
        max-width="600px"
      >
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
        >
          <v-card>
            <v-card-title>
              <span class="text-h5">{{ id ? $t('Edit Route Pass Plan') : $t('Add Route Pass Plan') }}</span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="name_en"
                      :rules="requiredRules"
                      label="English Name*"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="name_ar"
                      label="Arabic Name (Arabic)"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="period_type"
                      :items="periodOptions"
                      :rules="requiredRules"
                      :label="$t('Period Type') + '*'"
                      required
                      @change="periodTypeChanged"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="duration_days"
                      :rules="integerPositiveRules"
                      :label="$t('Duration Days') + '*'"
                      type="number"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="rides_limit"
                      :rules="integerPositiveRules"
                      :label="$t('Rides Limit') + '*'"
                      type="number"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="discount_percent"
                      :rules="percentRules"
                      :label="$t('Subscription Discount %') + '*'"
                      type="number"
                      step="0.01"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="sort_order"
                      :rules="nonNegativeRules"
                      label="Sort Order"
                      type="number"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" class="d-flex align-center">
                    <v-checkbox
                      v-model="is_active"
                      label="Active Plan"
                      dense
                      hide-details
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="description_en"
                      label="Description (EN)"
                      rows="2"
                      dense
                    ></v-textarea>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="description_ar"
                      label="Description (AR)"
                      rows="2"
                      dense
                    ></v-textarea>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="blue darken-1"
                text
                @click="planDialog = false"
              >
                {{ $t('cancel') }}
              </v-btn>
              <v-btn
                color="blue darken-1"
                text
                @click="savePlan"
              >
                {{ $t('Save Changes') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </v-dialog>
    </v-row>
  </div>
</template>

<script>
export default {
  data() {
    return {
      plans: [],
      isLoading: false,
      search: "",
      planDialog: false,
      valid: true,
      
      // Fields
      id: null,
      name_en: '',
      name_ar: '',
      period_type: '',
      duration_days: '',
      rides_limit: '',
      discount_percent: '',
      is_active: true,
      sort_order: 0,
      description_en: '',
      description_ar: '',

      periodOptions: [
        { text: this.$t('Weekly'), value: 'weekly' },
        { text: this.$t('Monthly'), value: 'monthly' },
        { text: this.$t('Quarterly'), value: 'quarterly' }
      ],

      requiredRules: [
        v => !!v || this.$t('required'),
      ],
      integerPositiveRules: [
        v => !!v || this.$t('required'),
        v => /^[0-9]+$/.test(v) || this.$t('must_be_greater_than_0'),
        v => parseInt(v) > 0 || this.$t('must_be_greater_than_0'),
      ],
      nonNegativeRules: [
        v => v === '' || v === null || /^[0-9]+$/.test(v) || 'Must be 0 or positive',
      ],
      percentRules: [
        v => !!v || this.$t('required'),
        v => !isNaN(parseFloat(v)) || 'Must be a number',
        v => parseFloat(v) >= 0 && parseFloat(v) <= 100 || 'Must be between 0 and 100',
      ],

      headers: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Plan Name", value: "name" },
        { text: "Period Type", value: "period_type" },
        { text: "Duration Days", value: "duration_days" },
        { text: "Rides Limit", value: "rides_limit" },
        { text: "Subscription Discount %", value: "discount_percent" },
        { text: "Status", value: "is_active" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    footerProps() {
      return {
        itemsPerPageText: this.$t("rows_per_page"),
      }
    }
  },
  mounted() {
    this.loadPlans();
  },
  methods: {
    loadPlans() {
      this.isLoading = true;
      axios
        .get(`/route-subscription-pass-plans`)
        .then((response) => {
          this.plans = response.data;
        })
        .catch((error) => {
          this.$notify({
            title: this.$t("Error"),
            text: "Error retrieving plans",
            type: 'error'
          });
          console.log(error);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    showPlanDialog() {
      this.id = null;
      this.name_en = '';
      this.name_ar = '';
      this.period_type = '';
      this.duration_days = '';
      this.rides_limit = '';
      this.discount_percent = '';
      this.is_active = true;
      this.sort_order = 0;
      this.description_en = '';
      this.description_ar = '';
      
      this.planDialog = true;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    periodTypeChanged(val) {
      // Apply defaults in UI as requested if creating a new plan
      if (this.id === null) {
        if (val === 'weekly') {
          this.duration_days = 7;
          this.rides_limit = 10;
          this.discount_percent = 10;
        } else if (val === 'monthly') {
          this.duration_days = 30;
          this.rides_limit = 40;
          this.discount_percent = 20;
        } else if (val === 'quarterly') {
          this.duration_days = 90;
          this.rides_limit = 120;
          this.discount_percent = 25;
        }
      }
    },
    editPlan(plan) {
      this.id = plan.id;
      this.name_en = plan.name_en;
      this.name_ar = plan.name_ar;
      this.period_type = plan.period_type;
      this.duration_days = plan.duration_days;
      this.rides_limit = plan.rides_limit;
      this.discount_percent = plan.discount_percent;
      this.is_active = !!plan.is_active;
      this.sort_order = plan.sort_order;
      this.description_en = plan.description_en;
      this.description_ar = plan.description_ar;
      this.planDialog = true;
    },
    toggleStatus(plan) {
      const newActiveState = !plan.is_active;
      axios
        .post(`/route-subscription-pass-plans/toggle-status`, {
          id: plan.id,
          is_active: newActiveState
        })
        .then(() => {
          plan.is_active = newActiveState;
          this.$notify({
            title: this.$t("Success"),
            text: this.$t("plan_status_updated"),
            type: 'success'
          });
        })
        .catch((error) => {
          this.$notify({
            title: this.$t("Error"),
            text: this.$t("plan_status_update_failed"),
            type: 'error'
          });
          console.log(error);
        });
    },
    savePlan() {
      if (this.$refs.form.validate()) {
        this.isLoading = true;
        this.planDialog = false;
        
        const payload = {
          name_en: this.name_en,
          name_ar: this.name_ar,
          period_type: this.period_type,
          duration_days: this.duration_days,
          rides_limit: this.rides_limit,
          discount_percent: this.discount_percent,
          is_active: this.is_active,
          sort_order: this.sort_order,
          description_en: this.description_en,
          description_ar: this.description_ar
        };

        const request = this.id 
          ? axios.post(`/route-subscription-pass-plans/${this.id}`, payload)
          : axios.post(`/route-subscription-pass-plans`, payload);

        request
          .then(() => {
            this.loadPlans();
            this.$notify({
              title: this.$t("Success"),
              text: this.id ? this.$t("plan_updated") : this.$t("plan_created"),
              type: 'success'
            });
          })
          .catch((error) => {
            this.$notify({
              title: this.$t("Error"),
              text: this.$t("plan_save_failed"),
              type: 'error'
            });
            console.log(error);
          })
          .then(() => {
            this.isLoading = false;
          });
      }
    },
    deletePlan(plan) {
      this.$swal
        .fire({
          title: this.$t("plan_delete_title"),
          text: this.$t("plan_delete_text", { name: this.$i18n.locale === 'ar' && plan.name_ar ? plan.name_ar : plan.name_en }),
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$t("yes_delete"),
          cancelButtonText: this.$t("cancel"),
        })
        .then((result) => {
          if (result.isConfirmed) {
            axios
              .delete(`/route-subscription-pass-plans/${plan.id}`)
              .then((response) => {
                this.loadPlans();
                this.$notify({
                  title: this.$t("Success"),
                  text: response.data.success ? response.data.success[0] : this.$t("plan_deleted"),
                  type: "success",
                  });
              })
              .catch((error) => {
                this.$notify({
                  title: this.$t("Error"),
                  text: this.$t("plan_delete_failed"),
                  type: 'error'
                });
                console.log(error);
              });
          }
        });
    }
  }
};
</script>
